import { zip } from 'fflate';

export interface ProfusionFolderSummary {
  folderName: string;
  fileCount: number;
  totalBytes: number;
}

function relativePath(file: File): string {
  const candidate = file.webkitRelativePath || file.name;
  const segments = candidate.replaceAll('\\', '/').split('/').filter(Boolean);
  if (!segments.length || segments.some((segment) => segment === '.' || segment === '..')) {
    throw new Error('La carpeta contiene una ruta no válida.');
  }
  return segments.join('/');
}

function safeArchiveName(folderName: string): string {
  const cleaned = folderName
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/g, '');
  return `${cleaned || 'estudio-profusion'}.zip`;
}

async function readFile(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer());
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error(`No fue posible leer ${file.name}.`));
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.readAsArrayBuffer(file);
  });
}

export function summarizeProfusionFolder(files: File[]): ProfusionFolderSummary {
  if (!files.length) throw new Error('Seleccione una carpeta de estudio Profusion.');
  const paths = files.map(relativePath);
  const firstPath = paths[0];
  if (!firstPath) throw new Error('Seleccione una carpeta de estudio Profusion.');
  const roots = new Set(paths.map((path) => path.split('/')[0]));
  if (roots.size !== 1) throw new Error('Seleccione una sola carpeta de estudio Profusion.');
  return {
    folderName: firstPath.split('/')[0] ?? 'estudio-profusion',
    fileCount: files.length,
    totalBytes: files.reduce((total, file) => total + file.size, 0),
  };
}

export async function createProfusionArchive(files: File[]): Promise<File> {
  const summary = summarizeProfusionFolder(files);
  const entries: Record<string, Uint8Array> = {};
  for (const file of files) {
    const path = relativePath(file);
    if (entries[path]) throw new Error(`La carpeta contiene una ruta duplicada: ${path}`);
    entries[path] = await readFile(file);
  }
  const archive = await new Promise<Uint8Array>((resolve, reject) => {
    zip(entries, { level: 0 }, (error, data) => {
      if (error) reject(error);
      else resolve(data);
    });
  });
  return new File([archive], safeArchiveName(summary.folderName), {
    type: 'application/zip',
    lastModified: Date.now(),
  });
}
